<script lang="ts">
	import {
		Modal,
		Text,
		LayerChartWrapper,
		LayerChartControls,
		ChartCard as LayerChartCard,
		ChartCardFullScreen as LayerChartCardFullScreen,
		type StatisticsData,
	} from '@Shelly/shelly-design-system/index';
	//Helpers
	import {
		buildChartControlsData,
		buildChartHeaderMenu,
		getInitLoadedPeriodStatistics,
		LAYER_RANGE_OPTIONS,
		type ChartMenuItem,
	} from 'src/helpers/statistics/ui/chart_control_helpers';
	import { getStatistics } from 'src/helpers/statistics/request/chart_get_statistics_helpers';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
	import { getChartSensorName, getDeviceTimeZoneDate } from 'src/helpers/statistics/core/chart_helper';
	import {
		resolveLatestChartValue,
		type DeviceStatusValue,
	} from 'src/helpers/statistics/ui/chart_latest_value_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	//Components
	import LayerChartStatistic from './LayerChartStatistic.svelte';
	import LayerChartCardSummary from './LayerChartCardSummary.svelte';
	import LayerChartSpacing from './LayerChartSpacing.svelte';
	//Stores
	import { user_data, user_settings } from 'src/stores';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { ChartConfig, ChartStatisticRangePeriod } from 'src/types/Chart';
	import type { Device } from 'src/types/Device';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';

	export let device: Device;

	const chartConfigs: ChartConfig[] = getDeviceChartsConfig(device)
		.map((i) => i.charts)
		.flat();

	const isPremiumUser = $user_data.premium || $user_settings.been_premium;
	const chartMenu: ChartMenuItem[] = buildChartHeaderMenu(chartConfigs);
	const timeZone = getDeviceTimeZoneDate(device);

	const onSelectPeriod = (
		currentPeriod: ChartStatisticRangePeriod,
		chartControlsData: ChartControlsDataInterface = null,
	) => {
		activePeriod = currentPeriod;
		const controlsData = chartControlsData || buildChartControlsData(device, currentPeriod);
		selectedChartControlsData = controlsData;
		statisticsData = getStatistics(
			device,
			controlsData,
			chartConfig,
			chartConfig.url,
			0,
			true,
		) as Promise<StatisticsData>;
	};

	const onControlsChange = (value: ChartControlsDataInterface) => {
		if (value?.['period'] != null) {
			const period = value['period'] as ChartStatisticRangePeriod;
			activePeriod = period;
			if (period !== 'custom') {
				onSelectPeriod(period);
			}
		} else if (value?.currentPeriod != null) {
			onSelectPeriod(value.currentPeriod as ChartStatisticRangePeriod, value);
		}
	};

	const onSelectChartType = (selectedIndex: number) => {
		selectedChartIndex = selectedIndex;
		chartConfig = chartConfigs[selectedChartIndex];
		onSelectPeriod(
			activePeriod,
			selectedChartControlsData?.currentPeriod === activePeriod ? selectedChartControlsData : null,
		);
	};

	let selectedChartIndex = 0;
	let chartConfig: ChartConfig = chartConfigs[selectedChartIndex];
	let selectedChartControlsData: ChartControlsDataInterface = null;
	let statisticsData: Promise<StatisticsData>;
	const configuredInitialPeriod =
		getInitLoadedPeriodStatistics(LAYER_RANGE_OPTIONS).period || LAYER_RANGE_OPTIONS[1].period;
	let activePeriod: ChartStatisticRangePeriod =
		!isPremiumUser && configuredInitialPeriod === 'hour' ? '24_hours' : configuredInitialPeriod;
	let showFullScreen = false;

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	$: deviceStatuses =
		status && status.online !== false
			? (status_configurator(status, device, settings) as DeviceStatusValue[])
			: [];

	onSelectPeriod(activePeriod);
</script>

<LayerChartControls
	{activePeriod}
	{isPremiumUser}
	{timeZone}
	chartControlID={`layer-main-${chartConfig.name}-${chartConfig.extid}`}
	onchange={onControlsChange}
/>
<div class:hour-period-chart={activePeriod === 'hour'}>
	<LayerChartWrapper
		{statisticsData}
		{chartMenu}
		{selectedChartIndex}
		class="mb-s"
		let:statisticsData
		onchange={onSelectChartType}
	>
		<LayerChartCard
			{chartMenu}
			{statisticsData}
			{selectedChartIndex}
			latestValue={resolveLatestChartValue(deviceStatuses, chartConfig, statisticsData.chartData)}
			showBottomLabels
			onFullscreen={() => (showFullScreen = true)}
			onchange={onSelectChartType}
		/>
		<LayerChartCardSummary {statisticsData} />
	</LayerChartWrapper>
</div>

<Modal
	class="full-screen-chart-modal"
	on:close={() => (showFullScreen = false)}
	showModal={showFullScreen}
	removeScrollPadding
	title={chartConfig?.chartTitle || ''}
	hideSlotToolbar={!chartConfig?.sensorID}
	type="default"
>
	<Text slot="toolbar" styleType="body" noWrap>
		{getChartSensorName(chartConfig)}
	</Text>
	<div slot="content" class="ml-m mr-m">
		<LayerChartControls
			{activePeriod}
			{isPremiumUser}
			{timeZone}
			chartControlID={`layer-full-${chartConfig.name}-${chartConfig.extid}`}
			onchange={onControlsChange}
		/>
		<div class:hour-period-chart={activePeriod === 'hour'}>
			<LayerChartWrapper
				class="mb-s"
				{statisticsData}
				{chartMenu}
				{selectedChartIndex}
				let:statisticsData
				onchange={onSelectChartType}
			>
				<LayerChartSpacing>
					<LayerChartCardFullScreen
						{chartMenu}
						{statisticsData}
						{selectedChartIndex}
						latestValue={resolveLatestChartValue(
							deviceStatuses,
							chartConfig,
							statisticsData.chartData,
						)}
						onchange={onSelectChartType}
					/>
				</LayerChartSpacing>
				<LayerChartStatistic chartStatistic={statisticsData.chartStatistic} />
				{#if statisticsData?.chartStatistic?.threeEMStatistic}
					{#each statisticsData.chartStatistic.threeEMStatistic as phase}
						<LayerChartStatistic chartStatistic={phase} />
					{/each}
				{/if}
			</LayerChartWrapper>
		</div>
	</div>
</Modal>

<style>
	:global(.modal-container.full-screen-chart-modal.type-default) {
		background: black !important;
	}

	.hour-period-chart :global(.lc-axis-bottom-tick-label) {
		transform: rotate(-40deg);
		transform-box: fill-box;
		transform-origin: center;
	}

	@media (min-width: 768px) {
		:global(.modal-container.full-screen-chart-modal.type-default) {
			width: 80% !important;
			max-height: 90vh !important;
		}
	}
</style>
